import { post } from 'common/src/vtj/browser/http'
import { Serialized } from 'common/src/vtj/types/util.type'
import { omit } from 'lodash'
import { runInAction, toJS } from 'mobx'
import { useMemo } from 'react'
import { DeclineAETRequestBody } from 'todistukset-common/src/vtj/schemas/aetRequestSchema'
import { AetSignerUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { TodistusPyyntöAet } from 'todistukset-common/src/vtj/types/todistus'
import RequestStatusEnum from 'todistukset-ui/src/vtj/RequestStatus.enum'
import {
  OnClickParams,
  OnNextClick,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import aetSharedSetters from 'todistukset-ui/src/vtj/stores/actions/aet-shared-setters'
import {
  handleError,
  setOrderRequestStatus,
} from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { refreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

type Form = AetSignerUiForm
type Setters = typeof setters
export type AetSignerFormActions = {
  [Prop in keyof Setters]: Setters[Prop] extends (
    form: Form,
    ...args: infer Args
  ) => void
    ? (...args: Args) => void
    : never
}

export function useAetSignerFormActions(): AetSignerFormActions {
  const form = useAetSignerForm()
  return useMemo(() => getAetSignerFormActions(form), [form])
}

export function getAetSignerFormActions(form: Form): AetSignerFormActions {
  return (Object.keys(setters) as (keyof Setters)[]).reduce(
    (actions, fnName) => {
      actions[fnName] = (...args: unknown[]) => {
        runInAction(() => {
          refreshSession()
          const setter = setters[fnName] as (
            form: Form,
            ...args: unknown[]
          ) => void
          setter(form, ...args)
        })
      }
      return actions
    },
    {} as AetSignerFormActions
  )
}

const setters = {
  ...aetSharedSetters,
}

export const useSubmitAetSignerForm: () => OnNextClick = () => {
  const { order, aetStore } = useStore()
  const form = useAetSignerForm()

  return async ({ onSuccess }: OnClickParams): Promise<void> => {
    if (order.requestStatus === RequestStatusEnum.SENDING) {
      return
    }

    setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENDING })

    try {
      const aetSignerForm = toJS(form)
      if (!aetSignerForm.aetTodistuspyynto) {
        return
      }
      const todistuspyyntoId = aetSignerForm.aetTodistuspyynto.requestId

      const formWithoutTodistuspyynto = omit(aetSignerForm, [
        'aetTodistuspyynto',
        'loadTodistuspyynto',
        'error',
        'isLoading',
      ])

      const path = `/api/restricted/todistukset/aet/${todistuspyyntoId}/approve`
      const [error] = await post<Serialized<TodistusPyyntöAet>>(
        path,
        formWithoutTodistuspyynto
      )

      if (error) {
        throw error
      }

      aetStore.loadAetTodistuspyynnot()

      setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENT })
      onSuccess()
    } catch (error) {
      const storePath = 'order.requestStatus'
      const errorValue = RequestStatusEnum.ERROR
      handleError({ error, storePath, errorValue })
    }
  }
}

export const useSubmitDeclineAetTodistuspyynto: (
  aetTodistuspyyntoId: number
) => (params: OnClickParams) => Promise<void> = (
  aetTodistuspyyntoId: number
) => {
  const { order, aetStore, aetSignerFormFn } = useStore()
  const form = aetSignerFormFn(aetTodistuspyyntoId)

  return async ({ onSuccess }: OnClickParams): Promise<void> => {
    if (
      order.requestStatus === RequestStatusEnum.SENDING ||
      !form.aetTodistuspyynto
    ) {
      return
    }

    setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENDING })

    try {
      const todistuspyyntoId = form.aetTodistuspyynto.requestId

      const body: DeclineAETRequestBody = {
        orderer: {
          hetu: form.orderer.hetu,
        },
      }

      const path = `/api/restricted/todistukset/aet/${todistuspyyntoId}/decline`
      const [error] = await post<Serialized<TodistusPyyntöAet>>(path, body)

      if (error) {
        throw error
      }

      aetStore.loadAetTodistuspyynnot()

      setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENT })
      onSuccess()
    } catch (error) {
      const storePath = 'order.requestStatus'
      const errorValue = RequestStatusEnum.ERROR
      handleError({ error, storePath, errorValue })
    }
  }
}
